import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import EventFormWithMap from './EventFormWithMap';
import EventDetailModal from './EventDetailModal';
import BulkEventUpload from './BulkEventUpload';
import EventCard from './EventCard';
import { 
  Calendar, 
  Plus, 
  Search,
  Upload
} from 'lucide-react';
import { DASHBOARD_CONFIG } from '../config/dashboard';
import Pagination from './Pagination';

interface Event {
  EVENT_ID?: number;
  id?: number;
  TITLE?: string;
  title?: string;
  DESCRIPTION?: string;
  description?: string;
  LOCATION?: string;
  location?: string;
  ADDRESS?: string;
  address?: string;
  LATITUDE?: number;
  latitude?: number;
  LONGITUDE?: number;
  longitude?: number;
  START_DATE?: string;
  startDate?: string;
  END_DATE?: string;
  endDate?: string;
  EXPECTED_ATTENDANCE?: number;
  expectedAttendance?: number;
  IS_VERIFIED?: boolean;
  isVerified?: boolean;
  IS_FEATURED?: boolean;
  isFeatured?: boolean;
  IS_TRENDING?: boolean;
  isTrending?: boolean;
  CREATED_BY?: number;
  createdBy?: number;
  CREATED_BY_ROLE?: string;
  createdByRole?: string;
  CREATED_AT?: string;
  createdAt?: string;
  UPDATED_AT?: string;
  updatedAt?: string;
  VERIFIED_BY?: number;
  verifiedBy?: number;
  IS_ACTIVE?: boolean;
  isActive?: boolean;
  IMAGES?: string[];
  images?: string[];
  IMAGE_URL?: string;
  imageUrl?: string;
}


interface AdminEventManagementProps {
  showHeader?: boolean;
}

const AdminEventManagement: React.FC<AdminEventManagementProps> = ({ showHeader = true }) => {
  const { user, token } = useAuth();
  
  // Helper function to safely get event ID
  const getEventId = (event: Event): number | undefined => {
    return event.EVENT_ID || event.id;
  };

  // Helper function to normalize event data for EventCard
  const normalizeEventForCard = (event: Event) => {
    return {
      id: event.EVENT_ID || event.id || 0,
      title: event.TITLE || event.title || 'Untitled Event',
      description: event.DESCRIPTION || event.description || 'No description available',
      location: event.LOCATION || event.location || 'Location not set',
      date: event.startDate && event.endDate
        ? `${new Date(event.startDate).toLocaleDateString('en-GB')} - ${new Date(event.endDate).toLocaleDateString('en-GB')}`
        : 'Date not set',
      // Image handling
      imageUrl: event.IMAGE_URL,
      IMAGE_URL: event.IMAGE_URL,
      images: event.IMAGES,
      IMAGES: event.IMAGES,
      // Status fields
      isFeatured: event.IS_FEATURED ?? event.isFeatured,
      isTrending: event.IS_TRENDING ?? event.isTrending,
      isVerified: event.IS_VERIFIED ?? event.isVerified,
      status: event.IS_VERIFIED ? 'approved' : 'pending',
      // Original event data for compatibility
      originalEvent: event
    };
  };

  // State variables
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(DASHBOARD_CONFIG.DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  
  // Bulk actions


  // Check if user has admin access
  const isAdmin = user?.role === 'ADMIN';

  // Fetch events
  const fetchEvents = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await apiService.getAdminEvents(token, {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sortBy: 'CREATED_AT',
        sortOrder: 'DESC'
      });
      
      // Handle response structure variations
      const eventsData = (response as any).events || (response.data && (response.data as any).events) || [];
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      const pagination = (response as any).pagination || (response.data && (response.data as any).pagination) || {};
      setTotalPages(Number(pagination.pages || pagination.totalPages || 1));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, pageSize, searchTerm, statusFilter]);

  // Load events on component mount and when dependencies change
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Handle event creation
  const handleCreateEvent = async (eventData: FormData) => {
    if (!token) return;
    
    setFormLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Helper: extract optional userData for create-with-user flow
      const extractUserData = (): {
        email: string;
        phone: string;
        firstName: string;
        lastName: string;
        role: string;
      } | null => {
        // 1) Try from FormData fields (if caller appended these keys programmatically)
        const fdEmail = (eventData.get('userEmail') as string) || '';
        const fdPhone = (eventData.get('userPhone') as string) || '';
        const fdFirstName = (eventData.get('userFirstName') as string) || '';
        const fdLastName = (eventData.get('userLastName') as string) || '';
        const fdRole = (eventData.get('userRole') as string) || '';
        if (fdEmail && fdPhone && fdFirstName && fdLastName && fdRole) {
          return { email: fdEmail, phone: fdPhone, firstName: fdFirstName, lastName: fdLastName, role: fdRole };
        }

        // 2) Try from URL query params (e.g., ?email=&phone=&firstName=&lastName=&role=)
        try {
          const params = new URLSearchParams(window.location.search);
          const qpEmail = params.get('email') || '';
          const qpPhone = params.get('phone') || '';
          const qpFirstName = params.get('firstName') || '';
          const qpLastName = params.get('lastName') || '';
          const qpRole = params.get('role') || '';
          if (qpEmail && qpPhone && qpFirstName && qpLastName && qpRole) {
            return { email: qpEmail, phone: qpPhone, firstName: qpFirstName, lastName: qpLastName, role: qpRole };
          }
        } catch (_) {}

        // 3) Try from sessionStorage (JSON string under key 'createWithUserUserData')
        try {
          const stored = sessionStorage.getItem('createWithUserUserData');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed && parsed.email && parsed.phone && parsed.firstName && parsed.lastName && parsed.role) {
              return {
                email: String(parsed.email),
                phone: String(parsed.phone),
                firstName: String(parsed.firstName),
                lastName: String(parsed.lastName),
                role: String(parsed.role)
              };
            }
          }
        } catch (_) {}

        return null;
      };


      const shouldUseCreateWithUser = user?.role === 'ADMIN';
      let potentialUserData = shouldUseCreateWithUser ? extractUserData() : null;

      // If not provided, use current admin as default userData (email optional)
      if (shouldUseCreateWithUser && !potentialUserData && user) {
        potentialUserData = {
          email: user.email || '',
          phone: user.phone || '',
          firstName: user.firstName || 'Admin',
          lastName: 'User',
          role: 'ADMIN'
        };
      }

      if (shouldUseCreateWithUser && potentialUserData) {
        // Create FormData for /api/create-with-user
        const createWithUserFormData = new FormData();
        
        // Add user data
        createWithUserFormData.append('userEmail', potentialUserData.email || '');
        createWithUserFormData.append('userPhone', potentialUserData.phone || '');
        createWithUserFormData.append('userFirstName', potentialUserData.firstName || '');
        createWithUserFormData.append('userLastName', potentialUserData.lastName || '');
        createWithUserFormData.append('userRole', potentialUserData.role || '');
        
        // Add event data
        createWithUserFormData.append('title', (eventData.get('title') as string) || '');
        createWithUserFormData.append('description', (eventData.get('description') as string) || '');
        createWithUserFormData.append('location', (eventData.get('location') as string) || '');
        
        // Convert dates to ISO string format
        const startDateValue = eventData.get('startDate') as string;
        const endDateValue = eventData.get('endDate') as string;
        if (startDateValue) {
          createWithUserFormData.append('startDate', new Date(startDateValue).toISOString());
        }
        if (endDateValue) {
          createWithUserFormData.append('endDate', new Date(endDateValue).toISOString());
        }
        
        createWithUserFormData.append('expectedAttendance', (eventData.get('expectedAttendance') as string) || '0');
        
        // Add coordinates if available
        const latitude = eventData.get('latitude');
        const longitude = eventData.get('longitude');
        if (latitude) createWithUserFormData.append('latitude', latitude as string);
        if (longitude) createWithUserFormData.append('longitude', longitude as string);
        
        // Add address if available
        const address = eventData.get('address');
        if (address) createWithUserFormData.append('address', address as string);
        
        // Add thumbnail file if available
        const thumbnail = eventData.get('thumbnail') as File | null;
        if (thumbnail instanceof File) {
          createWithUserFormData.append('thumbnail', thumbnail);
        }

        await apiService.createWithUser(createWithUserFormData, token);
      } else {
        // Fallback to existing create event endpoint
        await apiService.createEvent(eventData, token);
      }
      setSuccess('Event created successfully!');
      setShowCreateModal(false);
      fetchEvents();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create event');
      throw err; // Re-throw to handle in form
    } finally {
      setFormLoading(false);
    }
  };

  // Handle event editing
  const handleEditEvent = (event: Event) => {
    console.log('🔍 AdminEventManagement: Editing event:', event);
    setSelectedEvent(event);
    setShowEventModal(false);
    setShowEditModal(true);
  };

  // Handle event update
  const handleUpdateEvent = async (eventData: any) => {
    if (!selectedEvent || !token) return;
    
    setFormLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await apiService.updateEvent(selectedEvent.id || selectedEvent.EVENT_ID || 0, eventData, token);
      setSuccess('Event updated successfully!');
      setShowEditModal(false);
      setSelectedEvent(null);
      fetchEvents();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update event');
      throw err; // Re-throw to handle in form
    } finally {
      setFormLoading(false);
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async (eventId: number | undefined) => {
    if (!eventId || !token) return;
    
    try {
      await apiService.deleteEvent(eventId, token);
      setSuccess('Event deleted successfully!');
      fetchEvents();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete event');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle event verification
  const handleVerifyEvent = async (eventId: number | undefined, isVerified: boolean, verificationNotes?: string) => {
    // Check if eventId is valid
    if (!eventId) {
      setError('Invalid event ID. Cannot perform approval action.');
      return;
    }
    
    // Only admins and committee admins can verify events
    if (!isAdmin && user?.role !== 'COMMITTEE_ADMIN') {
      setError('Only administrators and community admins can approve/reject events');
      return;
    }
    
    try {
      await apiService.verifyEvent(eventId, { 
        isVerified, 
        verificationNotes: verificationNotes || (isVerified ? 'Event approved' : 'Event rejected')
      }, token!);
      setSuccess(`Event ${isVerified ? 'approved' : 'rejected'} successfully!`);
      fetchEvents();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update event approval status');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle event featuring
  const handleFeatureEvent = async (eventId: number | undefined, isFeatured: boolean) => {
    // Check if eventId is valid
    if (!eventId) {
      setError('Invalid event ID. Cannot feature/unfeature event.');
      return;
    }
    
    // Only admins and committee admins can feature events
    if (!isAdmin && user?.role !== 'COMMITTEE_ADMIN') {
      setError('Only administrators and community admins can feature events');
      return;
    }
    
    try {
      await apiService.featureEvent(eventId, isFeatured, token!);
      setSuccess(`Event ${isFeatured ? 'featured' : 'unfeatured'} successfully!`);
      fetchEvents();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update event featured status');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle event trending
  const handleTrendEvent = async (eventId: number | undefined, isTrending: boolean) => {
    // Check if eventId is valid
    if (!eventId) {
      setError('Invalid event ID. Cannot trend/untrend event.');
      return;
    }
    
    // Only admins and committee admins can mark events as trending
    if (!isAdmin && user?.role !== 'COMMITTEE_ADMIN') {
      setError('Only administrators and community admins can mark events as trending');
      return;
    }
    
    try {
      await apiService.trendEvent(eventId, isTrending, token!);
      setSuccess(`Event ${isTrending ? 'marked as trending' : 'removed from trending'} successfully!`);
      fetchEvents();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update event trending status');
      setTimeout(() => setError(null), 5000);
    }
  };









  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
        fetchEvents();
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle view event details
  const handleViewEventDetails = (event: Event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  // Handle bulk upload success
  const handleBulkUploadSuccess = () => {
    fetchEvents();
    setShowBulkUploadModal(false);
    setSuccess('Events successfully uploaded via CSV!');
    setTimeout(() => setSuccess(null), 3000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {showHeader && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Event Management</h2>
              <p className="text-gray-600">Create, edit, and manage all events</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowBulkUploadModal(true)}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                <Upload className="h-4 w-4" />
                  <span>Bulk Upload</span>
                </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 bg-dpn-pink hover:bg-dpn-raspberry text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Create Event</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
            </select>
                      <button
              type="submit"
              className="px-6 py-2 bg-dpn-pink hover:bg-dpn-raspberry text-white rounded-lg transition-colors"
            >
              Search
          </button>
          </div>
        </form>
        </div>



      {/* Events List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Events ({events.length})
            </h3>

          </div>
      </div>

        <div className="p-6">
        {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dpn-pink mx-auto mb-4"></div>
              <p className="text-gray-500">Loading events...</p>
          </div>
          ) : events.length === 0 ? (
            <div className="text-center py-8">
            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
              <p className="text-gray-500">Try adjusting your search criteria or create a new event.</p>
          </div>
        ) : (
            <>


              {/* Event Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event) => (
                  <div key={getEventId(event)} className="relative">

                    
                    {/* Event Card with Admin Actions */}
                    <EventCard
                      event={normalizeEventForCard(event)}
                      onViewDetails={handleViewEventDetails}
                      onEdit={handleEditEvent}
                      canEdit={isAdmin || event.CREATED_BY === user?.userId || event.createdBy === user?.userId}
                      user={user}
                      isAdmin={true}
                      onApprove={() => handleVerifyEvent(getEventId(event), true)}
                      onReject={() => handleVerifyEvent(getEventId(event), false)}
                      onFeature={() => handleFeatureEvent(getEventId(event), true)}
                      onUnfeature={() => handleFeatureEvent(getEventId(event), false)}
                      onTrend={() => handleTrendEvent(getEventId(event), true)}
                      onUntrend={() => handleTrendEvent(getEventId(event), false)}
                      onDelete={() => handleDeleteEvent(event.EVENT_ID || event.id)}
                    />
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination 
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  className="mt-6"
                />
              )}
            </>
                  )}
                </div>
              </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50">
          {success}
          </div>
        )}
      
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
          {error}
      </div>
      )}

      {/* Modals */}
      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <EventFormWithMap
              event={null}
              onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateEvent}
            loading={formLoading}
            title="Create New Event"
          />
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && selectedEvent && (
        <EventFormWithMap
          event={selectedEvent}
          onClose={() => {
            setShowEditModal(false);
            setSelectedEvent(null);
          }}
          onSubmit={handleUpdateEvent}
          loading={formLoading}
          title="Edit Event"
        />
      )}

      {/* Bulk Event Upload Modal */}
      {showBulkUploadModal && (
        <BulkEventUpload
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={handleBulkUploadSuccess}
        />
      )}

      {/* Event Details Modal */}
      <EventDetailModal
        event={selectedEvent}
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        onEdit={handleEditEvent}
        canEdit={true}
      />
    </div>
  );
};

export default AdminEventManagement; 
